import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';

export interface OverwriteField {
  label: string;
  existing: string;
  incoming: string;
  section?: string;
  laterality?: 'left' | 'right';
  /** Optional sub-section label rendered as a lighter divider within a section */
  subSection?: string;
}

export interface ScribeOverwriteDialogData {
  fields: OverwriteField[];
  /** @deprecated Pass additions as OverwriteField entries with empty `existing` instead */
  additions?: Array<{ label: string; value: string; subSection?: string }>;
}

export type OverwriteChoice = 'all' | 'new-only' | 'cancel';

/** A paired row for the signs table: one sign name, right-eye values, left-eye values */
export interface SignPairRow {
  label: string;
  right: { existing: string; incoming: string } | null;
  left: { existing: string; incoming: string } | null;
  /** true if either eye has a conflict (existing ≠ incoming) */
  hasConflict: boolean;
}

const SECTION_ORDER = [
  'patientProfile',
  'dewsScreening',
  'weather',
  'history',
  'medications',
  'symptoms',
  'signs',
  'assessment'
];

/**
 * Converts camelCase / PascalCase enum values into readable human labels.
 * e.g. "bluesGreensGrays"       -> "Blues, Greens, Grays"
 *      "hairColorBlond"         -> "Blond"
 *      "veryPale"               -> "Very Pale"
 *      "darkBrowns"             -> "Dark Browns"
 *      "paleBrownOlive"         -> "Pale Brown / Olive"
 *      "bluesGreensDarkGraysPers" -> "Blues, Greens, Dark Grays / Pers"
 *      "hairColorBrownDarkBlond"  -> "Brown / Dark Blond"
 *      "hairColorRedhead"         -> "Redhead"
 *      "hairColorBlack"           -> "Black"
 *      "hairColorDarkBrowns"      -> "Dark Browns"
 */
function formatEnumValue(value: string): string {
  if (!value) return value;

  // Handle gradeN pattern: "grade0" -> "Grade 0", "grade2" -> "Grade 2"
  const gradeMatch = value.match(/^grade(\d+)$/i);
  if (gradeMatch) {
    return 'Grade ' + gradeMatch[1];
  }

  // Strip known prefixes that are not part of the display label
  let v = value.replace(/^hairColor/, '');

  // Split on capital letters to get words
  const words = v
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(/\s+/);

  if (words.length <= 1) {
    // Single word — just title-case it
    return v.charAt(0).toUpperCase() + v.slice(1);
  }

  // Detect compound colour lists: multiple colour words joined together
  // Strategy: group consecutive colour-like words with commas, use "/" for qualifiers
  const colourWords = new Set([
    'Blues',
    'Greens',
    'Grays',
    'Greys',
    'Browns',
    'Blacks',
    'Pers',
    'Blue',
    'Green',
    'Gray',
    'Grey',
    'Brown',
    'Black'
  ]);
  const qualifierWords = new Set([
    'Dark',
    'Light',
    'Very',
    'Pale',
    'Blond',
    'Brunette',
    'Olive',
    'Pink'
  ]);

  // Title-case all words first
  const titled = words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

  // Build readable string: colour words separated by ", ", qualifiers attached to next word
  const parts: string[] = [];
  let i = 0;
  while (i < titled.length) {
    const w = titled[i];
    if (qualifierWords.has(w) && i + 1 < titled.length) {
      // Qualifier + next word form a compound (e.g. "Dark Grays", "Very Pale")
      parts.push(w + ' ' + titled[i + 1]);
      i += 2;
    } else {
      parts.push(w);
      i++;
    }
  }

  // If all parts are colour/qualifier combos, join with ", "
  // Otherwise fall back to space-joined title case
  const allColourLike = parts.every(p => {
    const first = p.split(' ')[0];
    return colourWords.has(first) || qualifierWords.has(first);
  });

  return allColourLike ? parts.join(', ') : parts.join(' ');
}

@Component({
  selector: 'csi-scribe-overwrite-dialog',
  template: `
    <h2 mat-dialog-title>
      <mat-icon style="vertical-align:middle;margin-right:6px;color:#d97706">warning</mat-icon>
      {{ 'aiScribe.overwriteDialog.title' | transloco }}
    </h2>

    <mat-dialog-content>
      <ng-container *ngIf="sections.length > 0">
        <p style="font-size:13px;color:#374151;margin:0 0 12px">
          {{ 'aiScribe.overwriteDialog.conflictDescription' | transloco }}
        </p>

        <ng-container *ngFor="let sec of sections">
          <!-- ── Section header ──────────────────────────────────────── -->
          <div class="section-header-block">
            <span class="section-header-label">{{
              'aiScribe.overwriteDialog.sectionLabels.' + sec.section | transloco
            }}</span>
          </div>

          <!-- ── Signs section: Right Eye / Left Eye table ──────────── -->
          <ng-container *ngIf="sec.section === 'signs'">
            <table class="overwrite-table signs-table">
              <thead>
                <tr>
                  <th class="field-col">{{ 'aiScribe.overwriteDialog.colField' | transloco }}</th>
                  <th colspan="2" class="eye-group-header">Right Eye</th>
                  <th colspan="2" class="eye-group-header">Left Eye</th>
                </tr>
                <tr class="sub-header-row">
                  <th class="field-col"></th>
                  <th class="eye-sub-col eye-border-left">
                    {{ 'aiScribe.overwriteDialog.colCurrentValue' | transloco }}
                  </th>
                  <th class="eye-sub-col">
                    {{ 'aiScribe.overwriteDialog.colAiValue' | transloco }}
                  </th>
                  <th class="eye-sub-col eye-border-left">
                    {{ 'aiScribe.overwriteDialog.colCurrentValue' | transloco }}
                  </th>
                  <th class="eye-sub-col">
                    {{ 'aiScribe.overwriteDialog.colAiValue' | transloco }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let p of sec.signPairs" [class.row-changed]="p.hasConflict">
                  <td class="field-col sign-label">
                    <mat-icon *ngIf="p.hasConflict" class="changed-icon">swap_horiz</mat-icon>
                    <span [matTooltip]="p.label">{{ p.label }}</span>
                  </td>
                  <td
                    class="eye-sub-col eye-border-left current-col"
                    [matTooltip]="p.right ? formatValue(p.right.existing) || '—' : '—'"
                  >
                    {{ p.right ? formatValue(p.right.existing) || '—' : '—' }}
                  </td>
                  <td
                    class="eye-sub-col incoming-col"
                    [matTooltip]="p.right ? formatValue(p.right.incoming) || '—' : '—'"
                  >
                    {{ p.right ? formatValue(p.right.incoming) || '—' : '—' }}
                  </td>
                  <td
                    class="eye-sub-col eye-border-left current-col"
                    [matTooltip]="p.left ? formatValue(p.left.existing) || '—' : '—'"
                  >
                    {{ p.left ? formatValue(p.left.existing) || '—' : '—' }}
                  </td>
                  <td
                    class="eye-sub-col incoming-col"
                    [matTooltip]="p.left ? formatValue(p.left.incoming) || '—' : '—'"
                  >
                    {{ p.left ? formatValue(p.left.incoming) || '—' : '—' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </ng-container>

          <!-- ── All other sections: standard 3-col table ───────────── -->
          <ng-container *ngIf="sec.section !== 'signs'">
            <table class="overwrite-table">
              <thead>
                <tr>
                  <th class="field-col">{{ 'aiScribe.overwriteDialog.colField' | transloco }}</th>
                  <th>{{ 'aiScribe.overwriteDialog.colCurrentValue' | transloco }}</th>
                  <th>{{ 'aiScribe.overwriteDialog.colAiValue' | transloco }}</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let f of sec.fields; let i = index">
                  <!-- Sub-section divider (e.g. Patient File within Patient Demographics) -->
                  <tr
                    *ngIf="
                      f.subSection && (i === 0 || sec.fields[i - 1].subSection !== f.subSection)
                    "
                    class="sub-section-header-row"
                  >
                    <td colspan="3" class="sub-section-header-cell">
                      <span class="sub-section-header-label">{{
                        'aiScribe.overwriteDialog.sectionLabels.' + f.subSection | transloco
                      }}</span>
                    </td>
                  </tr>
                  <tr
                    [class.row-changed]="f.existing && f.existing !== f.incoming"
                    [class.row-addition]="!f.existing"
                  >
                    <td class="field-col sign-col">
                      <mat-icon *ngIf="f.existing && f.existing !== f.incoming" class="changed-icon"
                        >swap_horiz</mat-icon
                      >
                      <mat-icon *ngIf="!f.existing" class="addition-icon"
                        >add_circle_outline</mat-icon
                      >
                      <span [matTooltip]="f.label">{{ f.label }}</span>
                    </td>
                    <td
                      class="current-col"
                      [matTooltip]="f.existing ? formatValue(f.existing) : '—'"
                    >
                      {{ f.existing ? formatValue(f.existing) : '—' }}
                    </td>
                    <td class="incoming-col" [matTooltip]="formatValue(f.incoming)">
                      {{ formatValue(f.incoming) }}
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </ng-container>
        </ng-container>
      </ng-container>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="choose('cancel')">
        {{ 'aiScribe.overwriteDialog.cancel' | transloco }}
      </button>
      <button mat-stroked-button (click)="choose('new-only')">
        {{ 'aiScribe.overwriteDialog.skipExisting' | transloco }}
      </button>
      <button mat-raised-button color="primary" (click)="choose('all')">
        {{ 'aiScribe.overwriteDialog.overwriteAll' | transloco }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      /* ── Base table ─────────────────────────────────────────────────── */
      .overwrite-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
        margin-bottom: 16px;
      }
      .overwrite-table th {
        text-align: left;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #6b7280;
        padding: 4px 8px 8px;
        border-bottom: 2px solid #e5e7eb;
        white-space: nowrap;
      }
      .overwrite-table td {
        padding: 6px 8px;
        border-bottom: 1px solid #f3f4f6;
        vertical-align: middle;
        word-break: break-word;
      }
      .field-col {
        min-width: 130px;
        width: 28%;
      }

      /* ── Section header block ───────────────────────────────────────── */
      .section-header-block {
        padding: 10px 8px 4px;
        background: #f9fafb;
        border-top: 1px solid #e5e7eb;
        margin-top: 4px;
      }
      .section-header-label {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: #6b7280;
      }

      /* ── Sub-section divider (e.g. Patient File) ────────────────────── */
      .sub-section-header-row td {
        padding: 8px 8px 3px;
        background: #f3f4f6;
        border-bottom: none;
      }
      .sub-section-header-cell {
        border-bottom: none !important;
      }
      .sub-section-header-label {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #9ca3af;
      }

      /* ── Signs table ────────────────────────────────────────────────── */
      .signs-table .eye-group-header {
        text-align: center;
        background: #f3f4f6;
        font-size: 11px;
        font-weight: 700;
        color: #374151;
        text-transform: none;
        letter-spacing: 0;
        padding: 6px 8px 4px;
      }
      .signs-table .sub-header-row th {
        font-size: 10px;
        color: #9ca3af;
        padding: 2px 8px 6px;
        border-bottom: 2px solid #e5e7eb;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .eye-border-left {
        border-left: 2px solid #e5e7eb !important;
      }
      .eye-sub-col {
        width: 17.5%;
      }

      /* ── Row states ─────────────────────────────────────────────────── */
      .row-changed {
        background: #fffbeb;
        border-left: 3px solid #d97706;
      }
      .row-changed .sign-col,
      .row-changed .sign-label {
        color: #92400e;
      }
      .row-changed .current-col {
        color: #9ca3af;
        text-decoration: line-through;
      }
      .row-changed .incoming-col {
        color: #007070;
        font-weight: 700;
      }
      .row-addition {
        background: #f0fdf4;
        border-left: 3px solid #059669;
      }
      .row-addition .sign-col {
        color: #065f46;
      }
      .row-addition .incoming-col {
        color: #059669;
      }

      /* ── Cell styles ────────────────────────────────────────────────── */
      .sign-col,
      .sign-label {
        font-weight: 600;
        color: #1f2937;
        display: flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
      }
      .current-col {
        color: #6b7280;
      }
      .incoming-col {
        color: #007070;
        font-weight: 600;
      }
      .changed-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: #d97706;
        flex-shrink: 0;
      }
      .addition-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: #059669;
        flex-shrink: 0;
      }

      mat-dialog-content {
        max-height: 60vh;
        overflow-y: auto;
        padding: 0 24px;
      }
    `
  ]
})
export class ScribeOverwriteDialogComponent {
  /** Ordered list of sections to render */
  sections: Array<{
    section: string;
    fields: OverwriteField[]; // for non-signs sections
    signPairs: SignPairRow[]; // for signs section
  }> = [];

  constructor(
    public dialogRef: MatDialogRef<ScribeOverwriteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ScribeOverwriteDialogData
  ) {
    // Merge legacy `additions` into the fields list as patientProfile entries with no existing value
    const additionFields: OverwriteField[] = (data.additions || []).map(a => ({
      label: a.label,
      existing: '',
      incoming: a.value,
      section: 'patientProfile',
      subSection: a.subSection
    }));

    const allFields = [...data.fields, ...additionFields];

    // Separate sign fields (have laterality) from all other fields
    const signFields = allFields.filter(f => f.section === 'signs' && f.laterality);
    const otherFields = allFields.filter(f => f.section !== 'signs' || !f.laterality);

    // Build paired sign rows
    const signMap = new Map<string, SignPairRow>();
    const signOrder: string[] = [];
    signFields.forEach(f => {
      if (!signMap.has(f.label)) {
        signMap.set(f.label, { label: f.label, right: null, left: null, hasConflict: false });
        signOrder.push(f.label);
      }
      const row = signMap.get(f.label)!;
      const side = { existing: f.existing, incoming: f.incoming };
      if (f.laterality === 'right') {
        row.right = side;
      } else {
        row.left = side;
      }
      if (f.existing && f.existing !== f.incoming) {
        row.hasConflict = true;
      }
    });
    const signPairs = signOrder.map(k => signMap.get(k)!);

    // Sort non-sign fields: conflicts first, then additions, within each section
    const sorted = otherFields.slice().sort((a, b) => {
      // Primary: section order
      const aIdx = SECTION_ORDER.indexOf(a.section || 'other');
      const bIdx = SECTION_ORDER.indexOf(b.section || 'other');
      if (aIdx !== bIdx) return aIdx - bIdx;
      // Secondary: sub-section (patientProfile fields before patientFile fields)
      const aHasSub = a.subSection ? 1 : 0;
      const bHasSub = b.subSection ? 1 : 0;
      if (aHasSub !== bHasSub) return aHasSub - bHasSub;
      // Tertiary: conflicts before additions
      const aDiff = a.existing && a.existing !== a.incoming ? 0 : 1;
      const bDiff = b.existing && b.existing !== b.incoming ? 0 : 1;
      return aDiff - bDiff;
    });

    // Group by section
    const groupMap = new Map<string, OverwriteField[]>();
    sorted.forEach(f => {
      const sec = f.section || 'other';
      if (!groupMap.has(sec)) groupMap.set(sec, []);
      groupMap.get(sec)!.push(f);
    });

    // Build ordered sections list
    const order = SECTION_ORDER.concat(
      Array.from(groupMap.keys()).filter(k => SECTION_ORDER.indexOf(k) === -1)
    );

    order.forEach(sec => {
      if (sec === 'signs' && signPairs.length > 0) {
        this.sections.push({ section: 'signs', fields: [], signPairs });
      } else if (groupMap.has(sec)) {
        this.sections.push({ section: sec, fields: groupMap.get(sec)!, signPairs: [] });
      }
    });
  }

  choose(result: OverwriteChoice) {
    this.dialogRef.close(result);
  }

  formatValue(value: string): string {
    return formatEnumValue(value);
  }
}
